import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, Inject, Input, NgModule, OnInit, Optional, ElementRef, ViewEncapsulation, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Utilities } from '@app/common/utilities';
import { ActionLinkModule } from '@app/components/actions/action-link/action-link.component';
import { ErrorTemplatesModule } from '@app/components/error-templates/error-templates.component';
import { TextPlaceholderModule } from '@app/components/text-placeholder/text-placeholder.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { ImgSrcProxyModule } from '@app/directives/img-src-proxy.directive';
import { NewsPage } from '@app/models/news-page';
import { LinkModule } from '@app/directives/link.directive';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { BaseTrackingService } from '@app/tracking/base-tracking.service';
import { TrackingEvent } from '@app/models/tracking-event';
import { Constants } from '@app/common/constants';
import { trigger, transition, style, animate } from '@angular/animations';
import { SwiperConfigInterface, SwiperModule } from 'ngx-swiper-wrapper';
import { ChannelService } from '@app/services/channel.service';
import { Subscription } from 'rxjs';
import { SimplifiedTerm } from '@app/models/simplified-term';

@Component({
  selector: 'app-compact-news-card-list',
  templateUrl: './compact-news-card-list.component.html',
  styleUrls: ['./compact-news-card-list.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateY(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-out', style({ transform: 'translateY(100%)' }))
      ])
    ])
  ],
  encapsulation: ViewEncapsulation.None,
})
export class CompactNewsCardListComponent implements OnInit, OnDestroy {

  @Input() personalNewsItems: Array<NewsPage>;
  @Input() titleMaxLength = 70;
  featureSettings = this.featureStore.state;
  objectFitFallback = false;
  showNewsDesc = this.featureStore.state.showNewsDescription;
  isQuickView = false;
  showChannelTagsPrintout = {};
  enableCompactDesign: boolean = this.featureStore.state.enableCompactDesign;
  channelSubscription: Subscription;
  channels: SimplifiedTerm[];

  config: SwiperConfigInterface = {
    // a11y: true,
    direction: 'horizontal',
    slidesPerView: 1,
    slideToClickedSlide: true,
    mousewheel: false,
    scrollbar: false,
    watchSlidesProgress: true,
    navigation: true,
    keyboard: true,
    pagination: true,
    centeredSlides: true,
    loop: true,
    roundLengths: true,
    // slidesOffsetBefore: 100,
    // slidesOffsetAfter: 100,
    spaceBetween: 10,
    breakpoints: {
        // when window width is >= 320px
        320: {
            slidesPerView: 1.1
        },
        576: {
          slidesPerView: 1
        }
    }
};

  constructor(@Inject(DOCUMENT) private document: Document,
              private featureStore: FeatureSettingsStoreService,
              public elementRef: ElementRef,
              private channelService: ChannelService,
              @Optional() private trackingService?: BaseTrackingService) {
  }

  ngOnInit(): void {
    if ('objectFit' in this.document.documentElement.style === false) {
      this.objectFitFallback = true;
    }
    this.channelSubscription = this.channelService.getChannels().subscribe(ch => this.channels = ch);
  }

  setFallBackStyle(newsItem: HTMLImageElement) {
    if (this.objectFitFallback) {
      if (newsItem) {
        newsItem.parentElement.style.backgroundImage = `url(${newsItem.src})`;
        newsItem.parentElement.style.backgroundSize = `cover`;
        newsItem.parentElement.style.backgroundPosition = `50% 50%`;
      }
      return {
        'display': `none`
      };
    }
  }

  showNewsDescription(newsItem: NewsPage) {
    if (newsItem.hide !== undefined) {
      newsItem.hide = !newsItem.hide;
    } else {
      newsItem.hide = true;
      if (this.trackingService && this.trackingService.trackEvent) {
        const trackingEvent = new TrackingEvent({
          name: Constants.TRACKING_EVENT_TYPE_NEWS_DESCR_CLICK,
          properties: {
            href: newsItem.Path,
            label: newsItem.Title,
            componentId: this.elementRef.nativeElement.localName
          }
        });
        this.trackingService.trackEvent(trackingEvent);
      }
    }
  }

  /**
   * Creates a user friendly string by concatenting the news item channel labels
   * @param newsItem Newsitem to get channels from
   * @returns string for displaying the tags for users
   */
  getChannelsString(newsItem: NewsPage) {
    if (newsItem.Channels && newsItem.Channels.length) {
      let channelsString = '';
      for (let i = 0; i < newsItem.Channels.length; i++) {
        if (i > 0) {
          channelsString += ', ';
        }
        // get the channel from channels with ChannelTermID used for WPL News
        const ch = this.channels.findRecursive(fc => fc.Guid === newsItem.Channels[i].ChannelTermId, 'Terms');
        // set channelName depending on how name can be found for channel
        const channelName =  newsItem.Channels[i].Label ? newsItem.Channels[i].Label : newsItem.Channels[i].ChannelTermId ? ch.Name : newsItem.Channels[i];
        channelsString += '#' + channelName;
      }
      return channelsString;
    }
    return null;
  }

  getSenderTagString(newsItem: NewsPage) {
    return Utilities.getSenderTagString(newsItem);
  }

  /**
   * Toggles if the news tags should be shown in the UI, needed since there is no title attribute on mobile devices
   * @param newsItem Newsitem to get channels from
   */
  showChannelTagsPrintoutClick(newsItem: NewsPage) {
    // Path is unique, no ID on the NewsPage object
    this.showChannelTagsPrintout[newsItem.Path] = !this.showChannelTagsPrintout[newsItem.Path];
  }

  getMobileClass() {
    return this.enableCompactDesign ? 'd-none d-lg-block d-md-block' : '';
  }

  onIndexChange(event) {
    console.log(event);
  }

  ngOnDestroy(): void {
    this.channelSubscription && this.channelSubscription.unsubscribe();
  }
}

@NgModule({
  declarations: [
    CompactNewsCardListComponent
  ],
  exports: [
    CompactNewsCardListComponent
  ],
  imports: [
    CommonModule,
    LinkModule,
    ImgSrcProxyModule,
    ErrorTemplatesModule,
    TextPlaceholderModule,
    WidgetPlaceholderModule,
    ActionLinkModule,
    MatButtonModule,
    SwiperModule
  ]
})
export class CompactNewsCardListModule { }
